<script lang="ts" setup>
import { getRootDomain, parseDomain } from '@thecompaniesapi/url-helpers'
import { ULink } from '#components'

const props = defineProps({
  active: {
    default: false,
    type: Boolean,
  },
  activeColor: {
    default: 'orange',
    type: String as PropType<StepCardActiveColor>,
  },
  clickable: {
    default: false,
    type: Boolean,
  },
  content: {
    default: '',
    type: String,
  },
  description: {
    default: '',
    type: String,
  },
  icon: {
    default: '',
    type: String,
  },
  logo: {
    default: '',
    type: String,
  },
  title: {
    default: '',
    type: String,
  },
  url: {
    default: '',
    type: String,
  },
})

const emit = defineEmits<{
  click: []
}>()

const domain = computed(() => {
  if (props.url) {
    return getRootDomain(props.url)
  }
})

const hostname = computed(() => {
  if (domain.value) {
    return parseDomain(domain.value).hostname
  }
})

function onClick() {
  if (props.clickable) {
    emit('click')
  }
}
</script>

<template>
  <component
    :is="url ? ULink : 'div'"
    class="flex flex-col gap-1 rounded-lg bg-gray-100 p-3 text-left text-sm text-gray-900 shadow ring-1 md:p-4 dark:bg-gray-900 dark:text-gray-100"
    :class="{
      'ring-gray-200 dark:ring-gray-800': !active,
      'bg-gray-50 ring-gray-400 dark:bg-gray-950 dark:bg-opacity-50 dark:ring-gray-600': active,
      'select-none': clickable,
      'cursor-pointer': clickable || url,
      'bg-opacity-50 transition hover:ring-gray-300 dark:hover:ring-gray-700': (clickable || url) && !active,
      'hover:bg-gray-50 hover:dark:bg-gray-950 dark:hover:bg-opacity-50': (clickable || url) && !active,
    }"
    :target="url.startsWith('http') ? '_blank' : undefined"
    :to="url"
    @click="onClick"
  >
    <div
      class="flex gap-3 text-sm"
      :class="{ 'items-center': title }"
    >
      <UAvatar
        v-if="logo || domain"
        :alt="logo || domain"
        :class="{ 'mt-1': !title }"
        size="md"
        :src="getLogo(logo || domain, 'companies')"
      />

      <div
        v-if="title"
        class="flex w-full flex-col overflow-hidden font-medium"
      >
        <span class="line-clamp-3 flex items-center gap-2 font-semibold">
          <UIcon
            v-if="icon"
            class="size-4.5 flex-initial flex-shrink-0"
            :class="{
              'text-amber-600 dark:text-amber-400': active && activeColor === 'amber',
              'text-blue-600 dark:text-blue-400': active && activeColor === 'blue',
              'text-cyan-600 dark:text-cyan-400': active && activeColor === 'cyan',
              'text-indigo-600 dark:text-indigo-400': active && activeColor === 'indigo',
              'text-orange-600 dark:text-orange-400': active && activeColor === 'orange',
              'text-purple-600 dark:text-purple-400': active && activeColor === 'purple',
              'text-red-600 dark:text-red-400': active && activeColor === 'red',
              'text-yellow-600 dark:text-yellow-400': active && activeColor === 'yellow',
            }"
            :name="icon"
          />

          <span class="flex-1">{{ title }}</span>

          <div
            v-if="$slots.afterTitle"
            class="flex-initial flex-shrink-0"
          >
            <slot name="afterTitle" />
          </div>
        </span>

        <p class="self-start truncate text-xs text-gray-600 dark:text-gray-400">
          {{ hostname }}
        </p>
      </div>

      <p
        v-if="content"
        class="flex-grow text-sm text-gray-900 md:text-base dark:text-gray-100"
      >
        {{ content }}
      </p>
    </div>

    <p
      v-if="description"
      class="flex-grow text-gray-700 dark:text-gray-300"
    >
      {{ description }}
    </p>
  </component>
</template>
